/**
 * 判断是否 十六进制颜色值.
 * 输入形式可为 #fff000 #f00
 *
 * @param   String  color   十六进制颜色值
 * @return  Boolean
 */
export function isHexColor(color: string) {
	const reg = /^#([0-9a-fA-F]{3}|[0-9a-fA-f]{6})$/;
	return reg.test(color);
}

/**
 * RGB 颜色值转换为 十六进制颜色值.
 * r, g, 和 b 需要在 [0, 255] 范围内
 *
 * @return  String          类似#ff00ff
 * @param r
 * @param g
 * @param b
 */
export function rgbToHex(r: number | string, g?: number, b?: number) {
	if (typeof r == 'string') {
		const rgbMatch = /^rgb|RGB\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/.exec(r);
		[r, g, b] = rgbMatch!.slice(1, 4).map(Number);
	}

	// tslint:disable-next-line:no-bitwise
	const hex = ((r << 16) | (g! << 8) | b!).toString(16);
	return `#${new Array(Math.abs(hex.length - 7)).join('0')}${hex}`;
}

/**
 * Transform a HEX color to its RGB representation
 * @param {string} hex The color to transform
 * @returns The RGB representation of the passed color
 */
export function hexToRGB(hex: string) {
	let sHex = hex.toLowerCase();
	if (isHexColor(hex)) {
		if (sHex.length === 4) {
			let sColorNew = '#';
			for (let i = 1; i < 4; i += 1) {
				sColorNew += sHex.slice(i, i + 1).concat(sHex.slice(i, i + 1));
			}
			sHex = sColorNew;
		}
		const sColorChange: number[] = [];
		for (let i = 1; i < 7; i += 2) {
			sColorChange.push(parseInt(`0x${sHex.slice(i, i + 2)}`));
		}
		return `RGB(${sColorChange.join(',')})`;
	}
	return sHex;
}

/**
 * 将RGB值转换为HSL值
 * @param {number} r 红色值 (0-255)
 * @param {number} g 绿色值 (0-255)
 * @param {number} b 蓝色值 (0-255)
 * @param {number} precision 精度，小数位数 (默认0，即四舍五入到整数)
 * @returns {number[]} [h, s, l] 数组，h为0-360度，s和l为0-100的百分比
 */
function rgbToHsl(r: number, g: number, b: number, precision = 0): [number, number, number] {
	r /= 255;
	g /= 255;
	b /= 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	let h = 0;
	let s = 0;
	const l = (max + min) / 2;

	if (max === min) {
		h = s = 0; // 无色彩
	} else {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			case g:
				h = (b - r) / d + 2;
				break;
			case b:
				h = (r - g) / d + 4;
				break;
		}
		h /= 6;
	}

	const roundToPrecision = (num: number) => {
		return precision === 0
			? Math.round(num)
			: Math.round(num * Math.pow(10, precision)) / Math.pow(10, precision);
	};

	return [roundToPrecision(h * 360), roundToPrecision(s * 100), roundToPrecision(l * 100)];
}

/**
 * 将颜色（rgba、rgb或hex）转换为HSL通道字符串格式
 * @param {string} color 颜色值，支持hex(#ffffff)、rgb(255,255,255)、rgba(255,255,255,0.5)格式
 * @param {number} precision 精度，小数位数 (默认2，保留两位小数)
 * @returns {string} HSL通道字符串，格式为 "212, 57.8%, 40%"
 */
export function colorToHslChannels(color: string, precision = 2): string {
	color = color.trim();
	let r: number, g: number, b: number;

	// 处理hex格式
	if (isHexColor(color)) {
		const rgb = hexToRGB(color)
			.replace(/(?:\(|\)|rgb|RGB)*/g, '')
			.split(',')
			.map((item) => Number(item.trim()));
		[r, g, b] = rgb;
	}
	// 处理rgb或rgba格式
	else if (color.startsWith('rgb')) {
		const rgbMatch = color.match(/rgba?\(([^)]+)\)/);
		if (rgbMatch) {
			const values = rgbMatch[1].split(',').map((item) => Number(item.trim()));
			[r, g, b] = values;
		} else {
			throw new Error('Invalid color format');
		}
	}
	// 处理其他可能的格式
	else {
		throw new Error('Unsupported color format. Please use hex, rgb, or rgba format.');
	}

	// 验证RGB值范围
	if (r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
		throw new Error('Invalid RGB values. Values must be between 0 and 255.');
	}

	const [h, s, l] = rgbToHsl(r, g, b, precision);

	// 格式化输出
	const formatValue = (value: number, unit = '') => {
		return value % 1 === 0 ? `${value}${unit}` : `${value.toFixed(precision)}${unit}`;
	};

	return `${formatValue(h)}, ${formatValue(s, '%')}, ${formatValue(l, '%')}`;
}
